import lineColumn from 'line-column';
import { GitSearchMatch, GitSearchResultItem } from '../models/git-worker.model';
import { WasmGitFS, WasmGitFSUtil, WorkerAction, WorkerActionError, WorkerFnParams } from '../wasm-git.worker.model';
import { FSAction } from './actions.enum';

export interface FSSearchAction extends WorkerAction {
  type: FSAction.Search;
  params: {
    paths: string[];
    query: string;
    matchCase: boolean;
    useRegExp: boolean;
  };
}

class FSSearchActionError extends WorkerActionError {}

export async function FSSearchActionFn({
  fs,
  params,
  util,
}: WorkerFnParams<FSSearchAction>): Promise<GitSearchResultItem[]> {
  const { paths, query, matchCase, useRegExp } = params;
  try {
    return (await Promise.all(paths.map((path) => searchInPath(path, query, useRegExp, matchCase, util, fs)))).filter(
      (item): item is GitSearchResultItem => !!item,
    );
  } catch (err) {
    throw new FSSearchActionError(err);
  }
}

function searchInPath(
  path: string,
  query: string,
  useRegExp: boolean,
  matchCase: boolean,
  util: WasmGitFSUtil,
  fs: WasmGitFS,
) {
  if (util.isDir(path)) {
    return null;
  }
  const queryRe = new RegExp(useRegExp ? query : escapeRegExp(query), `g${matchCase ? '' : 'i'}`);
  const content = fs.readFile(path, { encoding: 'utf8' });
  const matches = getMatchesInContent(content, queryRe);
  return matches.length
    ? {
        path,
        content,
        matches,
      }
    : null;
}

// https://stackoverflow.com/questions/3446170/escape-string-for-use-in-javascript-regex#answer-6969486
function escapeRegExp(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

function getMatchesInContent(content: string, queryRe: RegExp): GitSearchMatch[] {
  const lineColumnFinder = lineColumn(content);
  let matchResult: RegExpExecArray | null;
  const next = () => (matchResult = queryRe.exec(content));

  const matches: GitSearchMatch[] = [];

  next();
  while (matchResult!) {
    const { 0: match, index } = matchResult;

    const prevEndOfLine = content.lastIndexOf('\n', index);
    const leftPart = content
      .slice(prevEndOfLine > -1 ? prevEndOfLine + 1 : 0, index)
      .slice(-20)
      .trimLeft();

    const endOfLine = content.indexOf('\n', index);
    const rightPart = content
      .slice(index + match.length, endOfLine > -1 ? endOfLine : content.length)
      .slice(0, 20)
      .trimRight();
    const { line, col } = lineColumnFinder.fromIndex(index)!;

    matches.push({
      leftPart,
      match,
      rightPart,
      line,
      col,
    });

    next();
  }

  return matches;
}
